import { ApiError } from '@blackarrow/api-client';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { expireSession } from './session';

/**
 * Marks the mutations that talk to the auth endpoints.
 *
 * A rejected sign-in and an expired session both answer 401, and only one of
 * them means the token in hand has died. Without this, signing in with a wrong
 * password while holding a stale token from the *other* door would clear that
 * session and bounce you to the other door — which looks like the app losing
 * its mind rather than like a typo.
 */
export const AUTH_MUTATION_KEY = ['auth'] as const;

/**
 * The one place a dead session is noticed.
 *
 * It belongs on the caches rather than in a hook because it has to cover every
 * request, including the ones on screens nobody is looking at — the whole
 * failure mode is a console that keeps rendering against a token the server
 * has already stopped accepting.
 *
 * A full page load rather than a router navigation: everything in memory
 * belongs to a session that no longer exists, and reloading is the only way to
 * be certain none of it is still on screen. It costs one navigation on an
 * event that ends the session anyway.
 */
function signOutOnUnauthorized(error: unknown): void {
    if (!(error instanceof ApiError) || error.status !== 401) {
        return;
    }

    const to = expireSession(window.location.pathname + window.location.search);

    if (to !== null) {
        window.location.assign(to);
    }
}

/**
 * All server state lives here. Nothing goes in useState or a store —
 * see CLAUDE.md, "Frontend conventions".
 */
export const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: signOutOnUnauthorized }),

    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
            if (mutation.options.mutationKey?.[0] === AUTH_MUTATION_KEY[0]) {
                return;
            }

            signOutOnUnauthorized(error);
        },
    }),

    defaultOptions: {
        queries: {
            // Ops leaves the orders table open all day. Thirty seconds is
            // fresh enough to trust and slow enough not to hammer the box.
            staleTime: 30_000,
            refetchOnWindowFocus: true,

            retry: (failureCount, error) => {
                // A 4xx will fail again. Retrying it just delays the error the
                // user needs to see.
                if (error instanceof ApiError && error.status < 500) {
                    return false;
                }

                return failureCount < 2;
            },
        },

        mutations: {
            // Writes are idempotent server-side, but a blind retry can still
            // surprise someone — a failed write surfaces and the user decides.
            retry: false,
        },
    },
});
